import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { adminApi, type AdminUser } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ALL_ROLES = [
  "Requestor",
  "PodReviewer",
  "ProductManager",
  "Admin",
  "ReadOnly",
  "ReadOnlyFeature",
  "ReadOnlyDefect",
  "WriteDefect",
] as const;
type Role = (typeof ALL_ROLES)[number];

const ROLE_LABELS: Record<Role, string> = {
  Requestor: "Requestor",
  PodReviewer: "Pod Reviewer",
  ProductManager: "Product Manager",
  Admin: "Admin",
  ReadOnly: "Read Only (All)",
  ReadOnlyFeature: "Read Only (Feature)",
  ReadOnlyDefect: "Read Only (Defect)",
  WriteDefect: "Write Defect",
};

const ROLE_COLORS: Record<Role, string> = {
  Requestor:
    "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800",
  PodReviewer:
    "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800",
  ProductManager:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
  Admin:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
  ReadOnly:
    "bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700",
  ReadOnlyFeature:
    "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-800",
  ReadOnlyDefect:
    "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800",
  WriteDefect:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800",
};

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  Requestor: "Can submit new tech requests and track their own submissions.",
  PodReviewer: "Can review, approve, or reject requests assigned to their pod.",
  ProductManager:
    "Full dashboard access — can manage all requests, claim work, and view analytics.",
  Admin:
    "Everything a Product Manager can do, plus user management and system health. Assigned manually.",
  ReadOnly:
    "View-only dashboard access — can see all request types (Feature + Defect) but cannot submit, reply, or change anything.",
  ReadOnlyFeature:
    "View-only access scoped to Feature requests only. Cannot submit or reply.",
  ReadOnlyDefect:
    "View-only access scoped to Defect requests only. Cannot submit or reply.",
  WriteDefect:
    "Can submit Defect requests only. Cannot submit Feature requests, view the dashboard, or reply as a reviewer.",
};

const PAGE_SIZE = 10;

function RoleChip({
  role,
  active,
  onChange,
  disabled,
}: {
  role: Role;
  active: boolean;
  onChange: (role: Role, checked: boolean) => void;
  disabled: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(role, !active)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all select-none",
            active
              ? ROLE_COLORS[role]
              : "border-border bg-muted/30 text-muted-foreground",
            disabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer hover:opacity-80",
          )}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              active ? "bg-current" : "bg-muted-foreground/40",
            )}
          />
          {ROLE_LABELS[role]}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {ROLE_DESCRIPTIONS[role]}
        {role === "Admin" && (
          <span className="mt-0.5 block text-muted-foreground/70">
            Admin role can only be assigned directly in the database.
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [pendingRoles, setPendingRoles] = useState<string[]>(user.roles);
  const [dirty, setDirty] = useState(false);

  const mutation = useMutation({
    mutationFn: (roles: string[]) => adminApi.updateRoles(user.id, roles),
    onSuccess: (updated) => {
      qc.setQueryData<AdminUser[]>(["admin", "users"], (old) =>
        old?.map((u) => (u.id === updated.id ? updated : u)),
      );
      setPendingRoles(updated.roles);
      setDirty(false);
    },
  });

  const handleToggle = (role: Role, checked: boolean) => {
    if (role === "Admin") return;
    const next = checked
      ? [...pendingRoles.filter((r) => r !== role), role]
      : pendingRoles.filter((r) => r !== role);
    const final = next.length === 0 ? ["Requestor"] : next;
    setPendingRoles(final);
    setDirty(
      JSON.stringify(final.sort()) !== JSON.stringify([...user.roles].sort()),
    );
    // Never send Admin in the payload — the backend enforces Admin immutability,
    // but stripping it here prevents an empty-after-removal array from being
    // misinterpreted when the user only had ["Admin"].
    mutation.mutate(final.filter((r) => r !== "Admin"));
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/20 transition-colors">
      <td className="px-4 py-3">
        <p className="font-medium text-foreground text-sm">
          {user.display_name}
        </p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {ALL_ROLES.map((role) => (
            <RoleChip
              key={role}
              role={role}
              active={pendingRoles.includes(role)}
              onChange={handleToggle}
              disabled={mutation.isPending || role === "Admin"}
            />
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-right w-16">
        {mutation.isPending && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Saving…
          </span>
        )}
        {mutation.isError && (
          <span className="text-xs text-red-500">Failed</span>
        )}
        {mutation.isSuccess && !dirty && (
          <span className="text-xs text-emerald-600">Saved</span>
        )}
      </td>
    </tr>
  );
}

export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [guideOpen, setGuideOpen] = useState(false);

  const { data, isLoading, isError } = useQuery<AdminUser[]>({
    queryKey: ["admin", "users"],
    queryFn: () => adminApi.listUsers(),
    staleTime: 30_000,
  });

  const filtered = (data ?? []).filter((u) => {
    const q = search.toLowerCase();
    return (
      u.display_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">User Management</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Toggle roles on or off — changes save immediately.
        </p>
      </div>

      {/* Role guide — collapsible */}
      <div className="rounded-lg border bg-muted/30 overflow-hidden">
        <button
          type="button"
          onClick={() => setGuideOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
        >
          <span>Role Guide</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              guideOpen && "rotate-180",
            )}
          />
        </button>
        {guideOpen && (
          <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t">
            {ALL_ROLES.map((role) => (
              <div key={role} className="flex items-baseline gap-2.5 pt-3">
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-semibold",
                    ROLE_COLORS[role],
                    role === "Admin" && "opacity-60",
                  )}
                >
                  {ROLE_LABELS[role]}
                </span>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {ROLE_DESCRIPTIONS[role]}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full rounded-md border bg-card pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
        />
      </div>

      {isLoading && (
        <div className="text-sm text-muted-foreground animate-pulse">
          Loading users…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          Failed to load users. You may not have Admin access.
        </div>
      )}

      {data && (
        <TooltipProvider delayDuration={300}>
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    User
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Roles
                  </th>
                  <th className="px-4 py-2.5 w-16" />
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-8 text-center text-sm text-muted-foreground"
                    >
                      No users match &ldquo;{search}&rdquo;
                    </td>
                  </tr>
                ) : (
                  paged.map((user) => <UserRow key={user.id} user={user} />)
                )}
              </tbody>
            </table>

            {/* Footer: count + pagination */}
            <div className="border-t bg-muted/20 px-4 py-2 flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">
                {filtered.length} user{filtered.length !== 1 ? "s" : ""}
                {search ? ` matching "${search}"` : ""}
                {" · "}Filled chip = role active
              </span>

              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="rounded p-1 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-muted-foreground px-1">
                    {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="rounded p-1 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}
